import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import {
  LibraryItem,
  LibraryContentType,
  LibrariesJson,
  LIBRARIES_JSON_URLS,
  mapLibrariesJsonToItems
} from './library-items.data';

@Component({
  selector: 'app-libraries',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
  ],
  templateUrl: './libraries.component.html',
  styleUrl: './libraries.component.scss',
})
export class LibrariesComponent implements OnInit {
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Sandbox', route: '/sandbox' },
      { label: 'Library' }
    ],
    title: 'Library',
    description: 'A secure environment to explore, test, and integrate APIs, data services, and platform capabilities. A controlled testing space for partners and developers to build and validate agri-data solutions on NHA.'
  };

  allItems: LibraryItem[] = [];
  loading = true;
  loadError: string | null = null;
  activeTab: LibraryContentType = 'article';
  /** 'grid' = card grid (with thumbnails), 'table' = table rows. Grid is default for videos. */
  viewMode: 'grid' | 'table' = 'grid';

  readonly initialPageSize = 8;
  visibleCount = this.initialPageSize;

  private readonly tabQueryKey = 'tab';

  get filteredItems(): LibraryItem[] {
    return this.allItems.filter(item => item.type === this.activeTab);
  }

  get displayedItems(): LibraryItem[] {
    return this.filteredItems.slice(0, this.visibleCount);
  }

  get hasMoreItems(): boolean {
    return this.filteredItems.length > this.visibleCount;
  }

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Restore tab from URL so refresh keeps the user on the same tab (e.g. video)
    const tab = this.route.snapshot.queryParamMap.get(this.tabQueryKey);
    if (tab === 'video' || tab === 'article') {
      this.activeTab = tab;
    }
    this.route.queryParams.subscribe((params) => {
      const q = params[this.tabQueryKey];
      if (q === 'video' || q === 'article') {
        this.activeTab = q;
      }
    });
    this.tryLoadFromUrls(0);
  }

  private tryLoadFromUrls(index: number): void {
    if (index >= LIBRARIES_JSON_URLS.length) {
      this.loadError = 'Failed to load library content.';
      this.loading = false;
      return;
    }
    const url = LIBRARIES_JSON_URLS[index];
    this.http.get<LibrariesJson>(url).subscribe({
      next: (data) => {
        this.allItems = mapLibrariesJsonToItems(data);
        this.loading = false;
      },
      error: () => {
        this.tryLoadFromUrls(index + 1);
      }
    });
  }

  setTab(type: LibraryContentType): void {
    this.activeTab = type;
    this.visibleCount = this.initialPageSize;
    // Update URL so refresh keeps the user on this tab
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.tabQueryKey]: type },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  setViewMode(mode: 'grid' | 'table'): void {
    this.viewMode = mode;
  }

  loadMore(): void {
    this.visibleCount = Math.min(
      this.visibleCount + this.initialPageSize,
      this.filteredItems.length
    );
  }

  /** Fallback when thumbnail fails to load (e.g. staging CSP or network). */
  onThumbnailError(event: Event): void {
    const img = event?.target as HTMLImageElement | null;
    if (img) {
      img.style.background = '#f3f4f6';
      img.src = '';
    }
  }
}
